import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Link, useNavigate, useOutletContext } from 'react-router-dom'
import { canRoundAcceptBet, lotteryService } from '../../services/lotteryService'
import type { LotteryRound } from '../../services/lotteryService'
import type { LobbyHuayOutletContext } from './ModernLobbyHuayLayout'

/** หน้ายี่กี — โหลดรอบจาก API เหมือน user-classic YeekeePage, UI แบบ user-modern */
const ModernLobbyHuayYeekeePage: React.FC = () => {
  const { themeConfig } = useOutletContext<LobbyHuayOutletContext>()
  const navigate = useNavigate()
  const wipWupMinutes = themeConfig?.settings?.wipWupMinutes ?? 15
  const [rounds, setRounds] = useState<LotteryRound[]>([])
  const [loading, setLoading] = useState(true)
  const [countdown, setCountdown] = useState<Record<string, string>>({})

  const loadRounds = useCallback(async (cacheBust = false) => {
    try {
      setLoading(true)
      const todayRounds = await lotteryService.getTodayRounds(cacheBust)
      const yeekeeRounds = todayRounds.filter(
        (round) =>
          round.huayTypeName?.includes('ยี') ||
          round.huayName?.includes('ยี')
      )
      setRounds(yeekeeRounds)
    } catch (error) {
      console.error('Error loading Yeekee rounds:', error)
      setRounds([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadRounds()
    const id = setInterval(loadRounds, 30_000)
    return () => clearInterval(id)
  }, [loadRounds])

  useEffect(() => {
    const handleRoundUpdated = () => loadRounds(true)
    window.addEventListener('lottery_round_updated', handleRoundUpdated)
    return () => window.removeEventListener('lottery_round_updated', handleRoundUpdated)
  }, [loadRounds])

  useEffect(() => {
    if (rounds.length === 0) return
    const tick = () => {
      const next: Record<string, string> = {}
      rounds.forEach((round) => {
        next[round._id] = lotteryService.formatTimeRemaining(round.betEndTime)
      })
      setCountdown(next)
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [rounds])

  const sortedRounds = useMemo(() => {
    if (rounds.length === 0) return []
    const sortedByNumber = [...rounds].sort((a, b) => {
      const aNum = parseInt(a.roundNumber.split('-')[2] || '0', 10)
      const bNum = parseInt(b.roundNumber.split('-')[2] || '0', 10)
      return aNum - bNum
    })
    const now = new Date()
    const openRounds = sortedByNumber
      .filter((r) => canRoundAcceptBet(r))
      .map((r) => ({ ...r, diff: new Date(r.betEndTime).getTime() - now.getTime() }))
      .filter((r) => r.diff > 0)
    if (openRounds.length === 0) return sortedByNumber
    const currentRound = openRounds.sort((a, b) => a.diff - b.diff)[0]
    const currentRoundNum = parseInt(currentRound.roundNumber.split('-')[2] || '0', 10)
    const fromCurrent = sortedByNumber.filter((r) => parseInt(r.roundNumber.split('-')[2] || '0', 10) >= currentRoundNum)
    const beforeCurrent = sortedByNumber.filter((r) => parseInt(r.roundNumber.split('-')[2] || '0', 10) < currentRoundNum)
    return [...fromCurrent, ...beforeCurrent]
  }, [rounds])

  return (
    <>
      <div className="lobby-huay-page-header c_quinary">
        <Link to="/lobby-huay/listall" className="lobby-huay-page-header__back">
          <i className="fas fa-chevron-left" aria-hidden="true" />
          <span>กลับหน้าหลัก</span>
        </Link>
        <h3 className="lobby-huay-page-header__title c_tertiary font_md">
          จับยี่กี ({rounds.length} รอบ)
        </h3>
      </div>

      <div className="mt-3">
        {loading ? (
          <div className="text-center py-5">
            <i className="fas fa-spinner fa-spin fs-24 c_tertiary" />
            <p className="c_secondary mt-2">กำลังโหลดรอบยี่กี...</p>
          </div>
        ) : sortedRounds.length === 0 ? (
          <div className="text-center py-5 c_secondary">
            <p>ไม่มีรอบยี่กีในวันนี้</p>
          </div>
        ) : (
          <div className="row mx__1" style={{ rowGap: '0.5rem' }}>
            {sortedRounds.map((round, index) => {
              const isOpen = canRoundAcceptBet(round)
              const betEndDate = new Date(round.betEndTime)
              const closeTime = betEndDate.toLocaleString('th-TH', {
                timeZone: 'Asia/Bangkok',
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })
              const roundNum = round.roundNumber.split('-')[2] || String(index + 1)
              const countdownStr = countdown[round._id] ?? lotteryService.formatTimeRemaining(round.betEndTime)
              const minutesRemaining = (betEndDate.getTime() - Date.now()) / (1000 * 60)
              const isUrgent = isOpen && minutesRemaining <= 40 && minutesRemaining > 0
              const isLast15Min = isOpen && minutesRemaining <= wipWupMinutes && minutesRemaining > 0

              return (
                <div key={round._id} className="col-6 col-md-4 col-lg-3 px-1">
                  <div
                    className={`hh_hover bg_gd_quinary border_5 p-1 h-100 ${!isOpen ? 'h_close' : ''}`}
                    style={{
                      cursor: 'pointer',
                      ...(isUrgent && {
                        border: '2px solid var(--color-primary)',
                        boxShadow: '0 0 12px rgba(var(--color-primary-rgb, 0,0,0), 0.4)',
                      }),
                    }}
                    role="button"
                    onClick={() => {
                      if (isOpen) {
                        navigate('/lobby-huay/lottery-terms', {
                          state: { round, lotteryType: 'yeekee' as const },
                        })
                      } else {
                        navigate(`/lobby-huay/result?roundId=${round._id}`, { state: { round } })
                      }
                    }}
                  >
                    <div className="bg_op_tertiary border_5 p-1">
                      <div className="s_huay box_row bg_gd_quinary border_5 px-1 gap-2 justify-content-center">
                        <h6 className={`txt_sh c_tertiary mb-0 ${isLast15Min ? 'wip_wup' : ''}`}>รอบที่ {roundNum}</h6>
                      </div>
                      <h5 className={`text-center font_md mb-0 c_quaternary ${isLast15Min ? 'wip_wup' : ''}`} style={{ fontWeight: isUrgent ? 700 : 400 }}>
                        {countdownStr}
                      </h5>
                    </div>
                    <h5 className="text-center txt_mb font_rg c_tertiary mb-0 mt-1">ปิดรับ {closeTime}</h5>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </>
  )
}

export default ModernLobbyHuayYeekeePage
